import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '../../services/adminApi';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { urlEnum, urlIsoDate, urlOptionalId, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import { SectionFilterBar, TableCard, TableHeader, TableViewport, usePageSize } from '../table/TableKit';
import {
  EmptyRow,
  ErrorNotice,
  InstanceSelect,
  LoadingSpinner,
  SortableTh,
  inputClass,
  selectClass,
  tdClass,
} from './shared';
import RelativeTime from '../RelativeTime';

const URL_SPEC = {
  instance: urlOptionalId(),
  action: urlString(),
  resource: urlString(),
  from: urlIsoDate(),
  to: urlIsoDate(),
  q: urlString(),
  sort: urlEnum(['created_at', 'action', 'instance_key', 'resource_type', 'ip_address'] as const, 'created_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

function AdminAuditLog() {
  const [pageSize, setPageSize] = usePageSize('admin-audit-log');
  const [state, setState] = useUrlState(URL_SPEC);
  const [searchInput, setSearchInput] = useSearchDraft(state.q, (q) => setState({ q, page: 1 }));
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const offset = (state.page - 1) * pageSize;

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      instance_id: state.instance,
      action: state.action || undefined,
      resource_type: state.resource || undefined,
      from: state.from || undefined,
      to: state.to || undefined,
      search: state.q || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [pageSize, offset, state.instance, state.action, state.resource, state.from, state.to, state.q, state.sort, state.order],
  );

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['admin', 'auditLog', params],
    queryFn: () => adminApiService.getAuditLog(params),
    placeholderData: (prev) => prev,
    retry: false,
  });

  usePageClamp({
    page: state.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setState({ page }),
  });

  const onSort = (sort: string, order: 'asc' | 'desc') => setState({
    sort: sort as typeof state.sort,
    order,
    page: 1,
  });

  return (
    <div>
      {error && <ErrorNotice message="Failed to load audit log." />}
      <SectionFilterBar>
        <InstanceSelect
          value={state.instance}
          onChange={(instance) => setState({ instance, page: 1 })}
        />
        <select
          aria-label="Action filter"
          className={selectClass}
          value={state.action}
          onChange={(e) => setState({ action: e.target.value, page: 1 })}
        >
          <option value="">All Actions</option>
          {(data?.actions ?? []).map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select
          aria-label="Resource type filter"
          className={selectClass}
          value={state.resource}
          onChange={(e) => setState({ resource: e.target.value, page: 1 })}
        >
          <option value="">All Resource Types</option>
          {(data?.resource_types ?? []).map((rt) => (
            <option key={rt} value={rt}>{rt}</option>
          ))}
        </select>
        <input
          aria-label="From date"
          type="date"
          className={inputClass}
          value={state.from}
          onChange={(e) => setState({ from: e.target.value, page: 1 })}
        />
        <input
          aria-label="To date"
          type="date"
          className={inputClass}
          value={state.to}
          onChange={(e) => setState({ to: e.target.value, page: 1 })}
        />
      </SectionFilterBar>
      <TableCard>
        <TableHeader
          title="Audit Log"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search audit log',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setState({ page: 1 });
            },
          }}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <TableViewport>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh label="Time" sortKey="created_at" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Action" sortKey="action" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Instance" sortKey="instance_key" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Resource" sortKey="resource_type" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="IP" sortKey="ip_address" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(data?.items ?? []).map((entry) => (
                <React.Fragment key={entry.id}>
                  <tr
                    className={entry.request_data ? 'cursor-pointer hover:bg-body-bg/50' : ''}
                    onClick={() => entry.request_data && setExpandedId(expandedId === entry.id ? null : entry.id)}
                  >
                    <td className={`${tdClass} whitespace-nowrap`}><RelativeTime value={entry.created_at} /></td>
                    <td className={tdClass}>{entry.action}</td>
                    <td className={`${tdClass} font-mono max-w-[220px] truncate`} title={entry.instance_key ?? ''}>
                      {entry.instance_key || '—'}
                    </td>
                    <td className={tdClass}>
                      {entry.resource_type ? `${entry.resource_type}${entry.resource_id ? `: ${entry.resource_id}` : ''}` : '—'}
                    </td>
                    <td className={`${tdClass} font-mono`}>{entry.ip_address || '—'}</td>
                  </tr>
                  {expandedId === entry.id && entry.request_data && (
                    <tr>
                      <td colSpan={5} className="px-4 py-3 bg-body-bg/30">
                        <pre className="text-xs text-body-color whitespace-pre-wrap break-all font-mono">
                          {entry.request_data}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {(data?.items ?? []).length === 0 && <EmptyRow colSpan={5} text="No audit entries found." />}
            </tbody>
          </table>
          </TableViewport>
        )}
      </TableCard>
    </div>
  );
}

export default AdminAuditLog;
